import React, { useState, useEffect, useMemo, useCallback } from 'react';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import { useUnit } from '../contexts/UnitContext';
import { kaizenApi, resolveMediaUrl } from '../lib/api';
import { formatDate, formatDateTime } from '../lib/utils';
import { toast } from 'sonner';
import { Wrench, Search, AlertTriangle } from 'lucide-react';
import ParticipationSummary from '../components/ParticipationSummary';

// Last 24 months, newest first. Merged with whatever the server reports as
// having data, so a month with kaizens is always selectable even if it falls
// outside this window. Mirrors the Suggestions register.
function recentPeriods() {
  const out = [];
  const now = new Date();
  for (let i = 0; i < 24; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({
      period: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleString('en-US', { month: 'long', year: 'numeric' }),
    });
  }
  return out;
}
const currentPeriod = () => recentPeriods()[0].period;

function StatusPill({ status }) {
  const submitted = status === 'Submitted';
  return (
    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
      submitted ? 'bg-[#E7F7EF] text-[#15654B]' : 'bg-[#FBF0E6] text-[#B14A0E]'}`}>
      {status}
    </span>
  );
}

/** One kaizen, summarised. The full record (photos, benefits, cost saving) lives
 *  on the employee's Kaizen tab — this is the register view, so it shows what
 *  identifies the entry rather than reproducing the whole form. */
function KaizenCard({ k, onSheet }) {
  const before = resolveMediaUrl(k.before_photo);
  const after = resolveMediaUrl(k.after_photo);
  return (
    <div className="border border-[#F2F4F8] rounded-lg p-2.5 bg-[#FCFDFE]">
      <div className="flex items-start justify-between gap-2">
        <p className="text-[13px] font-medium text-[#11182B]">
          {/* Filing order for the month: #1 is whoever got theirs in first. */}
          {k.seq ? <span className="text-[11px] font-bold text-[#15307F] bg-[#E8EFFD] rounded px-1.5 py-0.5 mr-1.5">#{k.seq}</span> : null}
          {k.subject || 'Untitled kaizen'}
        </p>
        {k.kaizen_no && <span className="text-[11px] text-[#98A1B2] whitespace-nowrap">No. {k.kaizen_no}</span>}
      </div>
      {k.process && <p className="text-[12px] text-[#5A6377] mt-0.5">Process: {k.process}</p>}
      {/* `object-cover` used to crop these to a square, so a wide shop-floor
          photo lost both its ends and a tall one its top. Contained inside a
          fixed frame instead: same footprint every row, whole picture kept. */}
      {(before || after) && (
        <div className="flex gap-2 mt-2">
          {before && (
            <div className="w-20 h-20 rounded border border-[#E6E9F0] bg-[#F2F4F8] flex items-center justify-center overflow-hidden flex-none">
              <img src={before} alt="Before" data-testid="kaizen-thumb-before"
                className="max-w-full max-h-full w-auto h-auto object-contain" />
            </div>
          )}
          {after && (
            <div className="w-20 h-20 rounded border border-[#E6E9F0] bg-[#F2F4F8] flex items-center justify-center overflow-hidden flex-none">
              <img src={after} alt="After" data-testid="kaizen-thumb-after"
                className="max-w-full max-h-full w-auto h-auto object-contain" />
            </div>
          )}
        </div>
      )}
      <div className="flex items-center gap-2 mt-1.5 text-[11px] text-[#98A1B2] flex-wrap">
        <span>Recorded {formatDateTime(k.created_at)}</span>
        {k.implementation_date && <span>· Implemented {k.implementation_date}</span>}
        {k.total_cost_saving && <span className="text-[#15654B]">· Saving {k.total_cost_saving}</span>}
        {k.is_late && <span className="text-[#B14A0E] font-semibold">· Late</span>}
        <button onClick={() => onSheet(k)} data-testid={`kaizen-sheet-${k.id}`}
                className="text-[#2563EB] font-semibold hover:underline">· Sheet</button>
      </div>
    </div>
  );
}

export default function Kaizen() {
  const { activeUnit } = useUnit();

  const [period, setPeriod] = useState(currentPeriod);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [dept, setDept] = useState('');
  const [search, setSearch] = useState('');

  // The printed HR-F-16 sheet for one kaizen — the same rendering the
  // employee's Kaizen tab prints and the QR portal shows.
  const openSheet = useCallback(async (k) => {
    const w = window.open('', '_blank');
    try {
      const r = await kaizenApi.print(k.id, { logo_url: `${window.location.origin}/assets/aampl-logo.jpg` });
      if (w) { w.document.write(r.data.html); w.document.close(); }
    } catch (e) {
      if (w) w.close();
      toast.error('Could not open the sheet');
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await kaizenApi.monthly({ period, unit: activeUnit });
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load kaizens');
      setData(null);
    } finally { setLoading(false); }
  }, [period, activeUnit]);
  useEffect(() => { load(); }, [load]);

  const periodOptions = useMemo(() => {
    const seen = new Map();
    (data?.periods || []).forEach((p) => seen.set(p.period, p.label));
    recentPeriods().forEach((p) => { if (!seen.has(p.period)) seen.set(p.period, p.label); });
    return [...seen.entries()]
      .map(([p, label]) => ({ period: p, label }))
      .sort((a, b) => b.period.localeCompare(a.period));
  }, [data]);

  const depts = useMemo(
    () => [...new Set((data?.rows || []).map((r) => r.department).filter(Boolean))].sort(),
    [data],
  );

  const rows = useMemo(() => {
    let list = data?.rows || [];
    if (statusFilter) list = list.filter((r) => r.status === statusFilter);
    if (dept) list = list.filter((r) => r.department === dept);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((r) =>
        (r.name || '').toLowerCase().includes(q)
        || (r.card_no || '').toLowerCase().includes(q)
        || (r.kaizens || []).some((k) => `${k.subject || ''} ${k.process || ''}`.toLowerCase().includes(q)));
    }
    return list;
  }, [data, statusFilter, dept, search]);

  const summary = data?.summary || { total: 0, submitted: 0, pending: 0, kaizens: 0, unlinked: 0 };
  const pct = summary.total ? Math.round((summary.submitted / summary.total) * 100) : 0;

  const stat = (label, value, tone) => (
    <div className="bg-white border border-[#E6E9F0] rounded-xl px-4 py-3 flex-1 min-w-[130px]">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-[#98A1B2]">{label}</div>
      <div className={`text-2xl font-semibold tabular-nums mt-0.5 ${tone}`}>{value}</div>
    </div>
  );

  return (
    <div data-testid="kaizen-page" className="flex h-screen app-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 overflow-auto p-5 sm:p-6 lg:p-8 page-enter">
          <div className="max-w-[1200px] mx-auto">

            <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white"
                     style={{ background: 'linear-gradient(135deg,#0E6B8C,#38BDF8)' }}>
                  <Wrench className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold tracking-tight text-[#11182B]">Kaizen</h1>
                  <p className="text-sm text-[#5A6377]">
                    Month-wise register{activeUnit ? ` · ${activeUnit}` : ''} — who has submitted, who is still pending.
                  </p>
                </div>
              </div>
              <select data-testid="kaizen-period" value={period} onChange={(e) => setPeriod(e.target.value)}
                      className="border border-[#E6E9F0] rounded-md bg-white text-sm px-3 py-2 min-w-[180px]">
                {periodOptions.map((p) => <option key={p.period} value={p.period}>{p.label}</option>)}
              </select>
            </div>

            <ParticipationSummary fetcher={kaizenApi.summary} unit={activeUnit}
              noun="kaizen" nounPlural="Kaizens" accent="#38BDF8" />

            <h2 className="text-sm font-semibold text-[#11182B] mb-3">
              {data?.month_label || ''} register
              {data?.window && (
                <span className={`ml-2 text-[10px] font-semibold px-2 py-0.5 rounded-full align-middle ${
                  data.window.open ? 'bg-[#E7F7EF] text-[#15654B]' : 'bg-[#F2F4F8] text-[#5A6377]'}`}>
                  {data.window.open ? `Open till ${data.window.deadline_label}` : `Closed ${data.window.deadline_label}`}
                </span>
              )}
            </h2>

            <div className="flex gap-3 mb-5 flex-wrap">
              {stat('Employees', summary.total, 'text-[#11182B]')}
              {stat('Submitted', summary.submitted, 'text-[#15654B]')}
              {stat('Pending', summary.pending, 'text-[#B14A0E]')}
              {stat('Kaizens filed', summary.kaizens, 'text-[#11182B]')}
              {stat('Coverage', `${pct}%`, 'text-[#11182B]')}
            </div>

            {summary.unlinked > 0 && (
              <div data-testid="kaizen-unlinked" className="mb-4 flex items-start gap-2 text-[12px] text-[#B14A0E]
                                                            bg-[#FFF7E6] border border-[#FDD08A] rounded-md p-3">
                <AlertTriangle className="w-4 h-4 mt-[1px] flex-shrink-0" />
                <div>
                  <b>{summary.unlinked} kaizen{summary.unlinked === 1 ? '' : 's'} not linked to an employee.</b>{' '}
                  {summary.unlinked === 1 ? 'It names' : 'They name'} somebody only as free text, so
                  {summary.unlinked === 1 ? ' it counts' : ' they count'} in the total above but not toward
                  participation. Open the record and pick the employee to include
                  {summary.unlinked === 1 ? ' it' : ' them'}.
                  <div className="mt-1.5 space-y-1">
                    {(data?.unlinked_rows || []).map((k) => (
                      <div key={k.id} className="text-[#11182B]">
                        · {k.subject || 'Untitled'} — suggested by &ldquo;{k.suggested_by || 'not stated'}&rdquo;
                        {k.department ? ` (${k.department})` : ''}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-2 mb-4 flex-wrap items-center">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#98A1B2]" />
                <input value={search} onChange={(e) => setSearch(e.target.value)}
                       placeholder="Search name, card number or kaizen subject…"
                       className="w-full border border-[#E6E9F0] rounded-md bg-white text-sm pl-9 pr-3 py-2" />
              </div>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                      className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2">
                <option value="">All statuses</option>
                <option value="Submitted">Submitted only</option>
                <option value="Pending">Pending only</option>
              </select>
              <select value={dept} onChange={(e) => setDept(e.target.value)}
                      className="border border-[#E6E9F0] rounded-md bg-white text-sm px-2 py-2">
                <option value="">All departments</option>
                {depts.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>

            {loading ? (
              <div className="text-sm text-[#5A6377]">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="bg-white border border-[#E6E9F0] rounded-xl p-8 text-center text-sm text-[#5A6377]">
                No employees match these filters for {data?.month_label || 'this month'}.
              </div>
            ) : (
              <div className="bg-white border border-[#E6E9F0] rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#F7F8FB] text-[11px] uppercase tracking-wider text-[#5A6377]">
                      <th className="text-left font-semibold px-4 py-3 w-14" title="Who filed first this month">S.No</th>
                      <th className="text-left font-semibold px-4 py-3">Employee</th>
                      <th className="text-left font-semibold px-4 py-3 hidden md:table-cell">Department</th>
                      <th className="text-left font-semibold px-4 py-3">Status</th>
                      <th className="text-left font-semibold px-4 py-3 whitespace-nowrap">Submitted on</th>
                      <th className="text-left font-semibold px-4 py-3">Kaizen for {data?.month_label}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.employee_id} data-testid={`kaizen-row-${r.employee_id}`}
                          className="border-t border-[#F2F4F8] align-top">
                        <td className="px-4 py-3">
                          {/* Their place in the month's filing order — first kaizen in, #1. */}
                          <span className={`text-[12px] font-bold tabular-nums ${r.first_seq ? 'text-[#11182B]' : 'text-[#C7CEDB]'}`}>
                            {r.first_seq ? `#${r.first_seq}` : '—'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-[#11182B]">{r.name}</div>
                          <div className="text-[11px] text-[#98A1B2]">{r.card_no || '—'}</div>
                        </td>
                        <td className="px-4 py-3 text-[#5A6377] hidden md:table-cell">
                          <div>{r.department || '—'}</div>
                          <div className="text-[11px] text-[#98A1B2]">{r.designation || ''}</div>
                        </td>
                        <td className="px-4 py-3">
                          <StatusPill status={r.status} />
                          {r.is_late && (
                            <span data-testid="late-badge"
                              className="block mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full
                                         bg-[#FBF0E6] text-[#B14A0E] whitespace-nowrap w-fit">
                              Late
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {r.submitted_at ? (
                            <>
                              <div className="text-[13px] text-[#11182B] tabular-nums">{formatDate(r.submitted_at)}</div>
                              <div className="text-[11px] text-[#98A1B2] tabular-nums">
                                {formatDateTime(r.submitted_at).split(', ').slice(1).join(', ')}
                              </div>
                            </>
                          ) : (
                            <span className="text-[#98A1B2] text-[13px]">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {r.kaizens.length === 0 ? (
                            <span className="text-[#98A1B2] text-[13px]">Not submitted for this month</span>
                          ) : (
                            <div className="space-y-2">
                              {r.kaizens.map((k) => <KaizenCard key={k.id} k={k} onSheet={openSheet} />)}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
